import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  LogOut, Store, ChevronRight, Shield, Pencil, Plus, Sparkles,
  Crown, LifeBuoy, Trash2, Share2, Wallet,
} from "lucide-react";
import EditCustomerProfileSheet from "../components/EditCustomerProfileSheet";
import ShareSheet from "../components/ShareSheet";
import ProfileHeader from "../components/ProfileHeader";
import ReputationCardCompact from "../components/ReputationCardCompact";
import ProfileTabs from "../components/ProfileTabs";

/**
 * Profile — the owner's view of their own profile.
 * Hierarchy per Phase F spec:
 *   Banner + identity → Actions → Reputation → Tabs → Vendor tools → Admin tools
 */
export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [cosmetics, setCosmetics] = useState(null);
  const [publicProfile, setPublicProfile] = useState(null);

  const loadCosmetics = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/cosmetics/mine");
      setCosmetics(data.display || null);
    } catch { /* ignore */ }
  }, [user]);

  // We hit the public endpoint for our own user_id so the header shows the
  // same counts everyone else sees (posts / followers / following).
  const loadPublic = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const { data } = await api.get(`/users/${user.user_id}`);
      setPublicProfile(data);
    } catch { /* ignore */ }
  }, [user?.user_id]);

  useEffect(() => {
    loadCosmetics();
    loadPublic();
  }, [loadCosmetics, loadPublic]);

  if (!user) return null;

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  // Merge the private auth doc with the public projection so the redesigned
  // header can show everything (private fields like `wallet`, `is_creator`
  // stay from auth; public counts come from /users/{id}).
  const profile = {
    ...user,
    ...(publicProfile || {}),
    // Never let the public projection overwrite the private role/email.
    role: user.role,
    email: user.email,
    wallet: user.wallet,
    is_creator: user.is_creator,
    is_vip: user.is_vip,
  };

  const isApprovedVendor =
    user.role === "vendor" && user.is_vendor && user.is_approved !== false && !user.is_suspended;
  const canOpenAdmin = user.role === "admin" || user.role === "senior_admin" || user.role === "ceo" || user.role === "master_admin";
  const isMasterAdmin = user.role === "master_admin" || user.role === "ceo";

  const actions = (
    <>
      <Button
        onClick={() => navigate("/create-post")}
        className="rounded-full h-10 px-5 bg-pink-500 hover:bg-pink-400 text-white text-xs font-black glow-pink"
        data-testid="profile-create-post-btn"
      >
        <Plus className="w-3.5 h-3.5 mr-1.5" /> New post
      </Button>
      <Button
        onClick={() => setEditing(true)}
        className="rounded-full h-10 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-bold"
        data-testid="profile-edit-btn"
      >
        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
      </Button>
      <Button
        onClick={() => navigate("/profile/customize")}
        className="rounded-full h-10 px-4 bg-gradient-to-r from-pink-500/20 to-cyan-500/20 hover:from-pink-500/30 hover:to-cyan-500/30 border border-pink-400/30 text-white text-xs font-bold"
        data-testid="profile-customize-btn"
      >
        <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Customize
      </Button>
      <Button
        onClick={() => navigate("/wallet")}
        className="rounded-full h-10 px-4 bg-gradient-to-r from-yellow-400/20 to-pink-500/20 hover:from-yellow-400/30 hover:to-pink-500/30 border border-yellow-300/40 text-white text-xs font-bold"
        data-testid="profile-wallet-btn"
      >
        <Wallet className="w-3.5 h-3.5 mr-1.5" /> Wallet
      </Button>
      <Button
        onClick={() => setShareOpen(true)}
        className="rounded-full h-10 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-bold"
        data-testid="profile-share-btn"
      >
        <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
      </Button>
    </>
  );

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4 pb-24" data-testid="profile-page">
      <ProfileHeader
        profile={profile}
        isOwner
        actions={actions}
        equippedTitle={cosmetics?.title ? { label: cosmetics.title.label, color: cosmetics.title.color } : null}
      />

      {/* Reputation — compact, clickable, sits BELOW the identity per spec */}
      <ReputationCardCompact />

      {/* Content tabs */}
      <ProfileTabs userId={user.user_id} isOwner />

      {/* Vendor entry — approved vendor gets shop management */}
      {isApprovedVendor && (
        <div
          onClick={() => navigate("/sell")}
          className="rounded-2xl bg-gradient-to-br from-pink-500/15 to-transparent border border-pink-400/20 p-4 cursor-pointer hover:border-pink-400/40 transition-colors"
          data-testid="profile-shop-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-pink-300 font-black">My Shop</p>
              <p className="font-black text-lg mt-0.5">{user.vendor_profile?.business_name}</p>
              <p className="text-xs text-zinc-400 mt-0.5">
                {user.vendor_profile?.vendor_type} · {user.vendor_profile?.location || "—"}
              </p>
            </div>
            <ChevronRight className="text-pink-300" />
          </div>
        </div>
      )}

      {/* Regular user → onboarding entry point */}
      {!user.is_vendor && (
        <div
          onClick={() => navigate("/sell")}
          className="rounded-2xl bg-gradient-to-br from-cyan-400/15 to-transparent border border-cyan-400/20 p-4 cursor-pointer hover:border-cyan-400/40 transition-colors"
          data-testid="profile-become-vendor"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Become a vendor</p>
              <p className="font-black text-lg mt-0.5 flex items-center gap-2">
                <Store className="w-4 h-4" /> Open your shop
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">No fees. Direct payments.</p>
            </div>
            <ChevronRight className="text-cyan-300" />
          </div>
        </div>
      )}

      {/* Admin / Master Admin — visible ONLY on the owner's own view AND when
          they have the required role. Never leaks to public visitors. */}
      {canOpenAdmin && (
        <div
          onClick={() => navigate("/admin")}
          className={`rounded-2xl p-4 cursor-pointer transition-colors ${
            isMasterAdmin
              ? "bg-gradient-to-br from-amber-500/20 via-pink-500/10 to-transparent border-2 border-amber-400/50 hover:border-amber-400/70 shadow-[0_0_35px_-12px_rgba(245,158,11,0.6)]"
              : "bg-gradient-to-br from-amber-400/15 to-transparent border border-amber-400/30 hover:border-amber-400/50"
          }`}
          data-testid="profile-admin-link"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black inline-flex items-center gap-1">
                {isMasterAdmin ? (<><Crown className="w-3 h-3" /> Master Admin</>) : (<><Shield className="w-3 h-3" /> Admin</>)}
              </p>
              <p className="font-black text-lg mt-0.5">
                {isMasterAdmin ? "Master Admin Dashboard" : "Admin Dashboard"}
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                {isMasterAdmin
                  ? "Full platform control · tickets · moderation logs · admin roster."
                  : "Users, vendors, products, orders & customer tickets."}
              </p>
            </div>
            <ChevronRight className="text-amber-300" />
          </div>
        </div>
      )}

      {/* Support link for regular users */}
      {!canOpenAdmin && (
        <div
          onClick={() => navigate("/support")}
          className="rounded-2xl bg-gradient-to-br from-cyan-400/15 to-transparent border border-cyan-400/30 p-4 cursor-pointer hover:border-cyan-400/50 transition-colors"
          data-testid="profile-support-link"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black inline-flex items-center gap-1">
                <LifeBuoy className="w-3 h-3" /> Help
              </p>
              <p className="font-black text-base mt-0.5">Customer Support</p>
            </div>
            <ChevronRight className="text-cyan-300" />
          </div>
        </div>
      )}

      {/* Secondary owner-only utilities */}
      <div className="flex flex-wrap gap-2 text-xs">
        <Button
          onClick={() => navigate("/profile/trash")}
          className="rounded-full h-9 px-3 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 font-bold"
          data-testid="profile-trash-btn"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Trash bin
        </Button>
        <Button
          onClick={onLogout}
          className="rounded-full h-9 px-3 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 font-bold"
          data-testid="logout-btn"
        >
          <LogOut className="w-3.5 h-3.5 mr-1.5" /> Log out
        </Button>
      </div>

      <EditCustomerProfileSheet open={editing} onOpenChange={setEditing} />
      <ShareSheet open={shareOpen} onOpenChange={setShareOpen} />
    </div>
  );
}
